import {
  PagedResponse,
  QueryParams,
  ValidatedResponse,
} from '@ofqual/portal-core/core/types';
import {
  all,
  call,
  CallEffect,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { socService } from '../services';
import {
  ADD_CASE_NOTE,
  ActionPayload,
  ASSIGN_ITEM,
  loadedAoItem,
  loadedItem,
  loadedItems,
  loadedOfqualItem,
  LOADING_AO_ITEM,
  LOADING_ITEM,
  LOADING_ITEMS,
  LOADING_OFQUAL_ITEM,
  UNASSIGN_ITEM,
  updatedAoItem,
  updatedOfqualItem,
  UPDATING_AO_ITEM,
  UPDATING_OFQUAL_ITEM,
  UPDATING_REGULATORS_TO_NOTIFY_ITEM,
  SENDING_MESSAGE,
  ADD_EVENT,
  COMPLETE_EVENT,
  SEND_INTERNAL_MESSAGE,
  RESUBMIT_ITEM,
  SUBMIT_SOC_ITEM,
  CLOSE_ITEM,
  REOPEN_ITEM
} from "./actions";
import { authSelector } from "./selectors";
import { SocItemModel, RegulatorsToNotifyModel } from "../domain/SocItem";
import { SocEvent, SocEventComplete } from "../domain/SocEvent";
import { SocItemDetailsOfqual } from "../domain/SocItemDetailsOfqual";
import { SocItemDetailsAo } from "../domain/SocItemDetailsAo";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { SocSubmitStatus } from '../domain/SocSubmitStatus';


function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchAssignSaga() {
  yield takeLatest(ASSIGN_ITEM, assignItemSaga);
}
function* watchUnassignSaga() {
  yield takeLatest(UNASSIGN_ITEM, unassignItemSaga);
}

function* watchLoadingOfqualItemSaga() {
  yield takeLatest(LOADING_OFQUAL_ITEM, loadingOfqualItemDetailsSaga);
}

function* watchUpdatingOfqualItemSaga() {
  yield takeLatest(UPDATING_OFQUAL_ITEM, updateOfqualItemSaga);
}

function* watchLoadingAoItemSaga() {
  yield takeLatest(LOADING_AO_ITEM, loadingAoItemDetailsSaga);
}

function* watchUpdatingAoItemSaga() {
  yield takeLatest(UPDATING_AO_ITEM, updateAoItemSaga);
}

function* watchRegulatorsToNotifyItemSaga() {
  yield takeLatest(
    UPDATING_REGULATORS_TO_NOTIFY_ITEM,
    updateRegulatorsToNotifySaga,
  );
}

function* watchAddCaseNoteSaga() {
  yield takeLatest(ADD_CASE_NOTE, addCaseNoteSaga);
}

function* watchSendMessageSaga() {
  yield takeEvery(SENDING_MESSAGE, sendMessageSaga);
}

function* watchAddEventSaga() {
  yield takeLatest(ADD_EVENT, addEventSaga);
}

function* watchCompleteEventSaga() {
  yield takeLatest(COMPLETE_EVENT, completeEventSaga);
}

function* watchSendInternalMessageSaga() {
  yield takeLatest(SEND_INTERNAL_MESSAGE, sendInternalMessage);
}

function* watchEnableResubmitSaga() {
  yield takeLatest(RESUBMIT_ITEM, enableResubmitSaga);
}

function* watchSubmitSocSaga() {
  yield takeLatest(SUBMIT_SOC_ITEM, submitSocItemSaga);
}

function* watchCloseSocSaga() {
  yield takeLatest(CLOSE_ITEM, closeSocSaga)
}

function* watchReopenSocSaga() {
  yield takeLatest(REOPEN_ITEM, reopenSocSaga)
}

function* itemsSaga({ payload }: ActionPayload<any>) {
  const { queryParams, year } = payload as {
    queryParams: QueryParams;
    year: string;
  };
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<SocItemModel> = yield call(
    [socService, socService.search],
    queryParams,
    year,
    { token: auth.token },
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, year } = payload as { id: string; year: string };

  const item: SocItemModel = yield call(
    [socService, socService.get],
    id,
    year,
    { token: auth.token },
  );
  yield put(loadedItem(item));
}

function* assignItemSaga({
  payload,
}: ActionPayload<{
  id: string;
  year: string;
  upn: string;
  displayName: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, year, upn, displayName } = payload;

  try {
    const soc: SocItemModel = yield call(
      [socService, socService.assign],
      id,
      year,
      upn,
      displayName,
      { token },
    );
    yield put(loadedItem(soc));
  } catch (err: any) {
    throw err;
  }
}

function* unassignItemSaga({
  payload,
}: ActionPayload<{
  id: string;
  year: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, year } = payload;

  try {
    const soc: SocItemModel = yield call(
      [socService, socService.unassign],
      id,
      year,
      { token },
    );
    yield put(loadedItem(soc));
  } catch (err: any) {
    throw err;
  }
}

function* loadingOfqualItemDetailsSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, year } = payload as { id: string; year: string };

  const ofqualItemDetails: SocItemDetailsOfqual = yield call(
    [socService, socService.getOqualItemDetails],
    id,
    year,
    { token: auth.token },
  );
  yield put(loadedOfqualItem(ofqualItemDetails));
}

function* updateOfqualItemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { year, model } = payload as {
    year: string;
    model: SocItemDetailsOfqual;
  };

  const validatedResponse: ValidatedResponse<SocItemDetailsOfqual> = yield call(
    [socService, socService.putOqualItemDetails],
    year,
    model,
    { token: auth.token },
  );
  if (validatedResponse.isValid) {
    yield put(updatedOfqualItem(validatedResponse.data));
  }
}

function* loadingAoItemDetailsSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, year } = payload as { id: string; year: string };

  const aoItemDetails: SocItemDetailsAo = yield call(
    [socService, socService.getAoItemDetails],
    id,
    year,
    { token: auth.token },
  );
  yield put(loadedAoItem(aoItemDetails));
}

function* updateAoItemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { year, model } = payload as { year: string; model: SocItemDetailsAo };
  var calls: CallEffect[] = [];

  // Load calls into a list if a file is present
  if (model.accountStatementAttachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'AccountStatement',
        model.socId,
        model.accountStatementAttachment!,
        { token: auth.token },
      ),
    );

  if (model.kloe1Attachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'KLOE1',
        model.socId,
        model.kloe1Attachment!,
        { token: auth.token },
      ),
    );

  if (model.kloe2Attachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'KLOE2',
        model.socId,
        model.kloe2Attachment!,
        { token: auth.token },
      ),
    );

  if (model.kloe3Attachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'KLOE3',
        model.socId,
        model.kloe3Attachment!,
        { token: auth.token },
      ),
    );

  // Handle all of the uploads
  yield all(calls);

  // Then updated the main details
  const validatedResponse: ValidatedResponse<SocItemDetailsAo> = yield call(
    [socService, socService.putAoItemDetails],
    year,
    model,
    { token: auth.token },
  );
  if (validatedResponse.isValid) {
    yield put(updatedAoItem(validatedResponse.data));
  }
}

function* updateRegulatorsToNotifySaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, year, model } = payload as {
    id: string;
    year: string;
    model: RegulatorsToNotifyModel;
  };

  const validatedResponse: ValidatedResponse<SocItemModel> = yield call(
    [socService, socService.putRegulatorsToNotify],
    id,
    year,
    model,
    { token: auth.token },
  );
  if (validatedResponse.isValid) {
    yield put(loadedItem(validatedResponse.data));    
  }
}

function* addCaseNoteSaga({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  note: string;
  noteType: number;
}>) {
  const { token } = yield select(authSelector);
  const { id, files, note, noteType } = payload;
  try {
    yield call(
      [socService, socService.addCaseNote],
      id,
      files,
      note,
      noteType,
      { token },
    );
    yield put(refreshActivityFeed(id));
  } catch (err: any) {
    throw err;
  }
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ id: string; files: File[]; message: string }>) {
  const { token } = yield select(authSelector);
  const { id, files, message } = payload;
  try {
    const soc: SocItemModel = yield call(
      [socService, socService.sendMessage],
      id,
      files,
      message,
      {
        token,
      },
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(soc));
  } catch (err: any) {
    throw err;
  }
}

function* addEventSaga({
  payload,
}: ActionPayload<{ id: string; newEvent: SocEvent }>) {
  const { token } = yield select(authSelector);
  const { id, newEvent } = payload;
  try {
    const item: SocItemModel = yield call(
      [socService, socService.addEvent],
      id,
      newEvent,
      { token },
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(item));
  } catch (err: any) {
    throw err;
  }
}

function* completeEventSaga({
  payload,
}: ActionPayload<{
  id: string;
  eventId: string;
  completedEvent: SocEventComplete;
}>) {
  const { token } = yield select(authSelector);
  const { id, eventId, completedEvent } = payload;
  try {
    const item: SocItemModel = yield call(
      [socService, socService.completeEvent],
      id,
      eventId,
      completedEvent,
      { token },
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(item));
  } catch (err: any) {
    throw err;
  }
}

function* sendInternalMessage({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  subject: string;
  message: string;
  recipients: string[];
}>) {
  const { token } = yield select(authSelector);
  const { id, files, subject, message, recipients } = payload;
  try {
    yield call(
      [socService, socService.sendInternalMessage],
      id,
      files,
      subject,
      message,
      recipients,
      { token },
    );
    yield put(refreshActivityFeed(id));
  } catch (err: any) {
    throw err;
  }
}

function* enableResubmitSaga({
  payload,
}: ActionPayload<{ id: string; year: string }>) {
  const { token } = yield select(authSelector);
  const { id, year } = payload as { id: string; year: string };
  
  const soc: SocItemModel = yield call(
    [socService, socService.enableResubmit],
    id,
    year,
    { token },
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(soc));
}

function* submitSocItemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { year, modelAo, modelRegulator, saveAction } = payload as { year: string; modelAo: SocItemDetailsAo; modelRegulator: RegulatorsToNotifyModel; saveAction: SocSubmitStatus };
  var calls: CallEffect[] = [];

  // Load calls into a list if a file is present
  if (modelAo.accountStatementAttachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'AccountStatement',
        modelAo.socId,
        modelAo.accountStatementAttachment!,
        { token: auth.token },
      ),
    );

  if (modelAo.kloe1Attachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'KLOE1',
        modelAo.socId,
        modelAo.kloe1Attachment!,
        { token: auth.token },
      ),
    );

  if (modelAo.kloe2Attachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'KLOE2',
        modelAo.socId,
        modelAo.kloe2Attachment!,
        { token: auth.token },
      ),
    );

  if (modelAo.kloe3Attachment != null)
    calls.push(
      call(
        [socService, socService.updateActiveFileSubmission],
        year,
        'KLOE3',
        modelAo.socId,
        modelAo.kloe3Attachment!,
        { token: auth.token },
      ),
    );
  // Handle all of the uploads
  yield all(calls);

  // Then updated the main ao details
  const validatedResponseAo: ValidatedResponse<SocItemDetailsAo> = yield call(
    [socService, socService.putAoItemDetails],
    year,
    modelAo,
    { token: auth.token },
  );

  const validatedResponseRegulators: ValidatedResponse<SocItemModel> = yield call(
    [socService, socService.putRegulatorsToNotify],
    modelAo.socId,
    year,
    modelRegulator,
    { token: auth.token },
  );


  if (validatedResponseAo.isValid && validatedResponseRegulators.isValid) {
    //check if we need to submit the soc note we only want to do it once and I decided on this one
    if (saveAction === SocSubmitStatus.SaveSubmitPass) {
      yield call(
        [socService, socService.SubmitSoc],
        modelAo.socId,
        year,
        { token: auth.token },
      );
      yield put(updatedAoItem(validatedResponseAo.data));

      const item: SocItemModel = yield call(
        [socService, socService.get],
        modelAo.socId,
        year,
        { token: auth.token },
      );
      yield put(loadedItem(item));
    }
  }
}

function* closeSocSaga({
  payload
}: ActionPayload<{ id: string, year:string, closeReason:string }>) {
  const { token } = yield select(authSelector);
  const { id, year, closeReason } = payload;

  try {
    const validatedResponse: ValidatedResponse<SocItemModel> = yield call(
      [socService, socService.close],
      id,
      year,
      closeReason,
      {
        token,
      }
    );

    if (validatedResponse.isValid) {
      yield put(refreshActivityFeed(id));
      yield put(loadedItem(validatedResponse.data));
    } 
  }
  catch (err: any) {
    throw err;
  }
}

function* reopenSocSaga({
  payload
}: ActionPayload<{ id: string, year: string }>) {
  const { token } = yield select(authSelector);
  const { id, year } = payload;

  try {
    const validatedResponse: ValidatedResponse<SocItemModel> = yield call(
      [socService, socService.reopen],
      id,
      year,  
      {
        token,
      }
    );

    if (validatedResponse.isValid) {
      yield put(refreshActivityFeed(id));
      yield put(loadedItem(validatedResponse.data));
    } 
  }
  catch (err: any) {
    throw err;
  }
}


export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchAssignSaga(),
    watchUnassignSaga(),
    watchLoadingOfqualItemSaga(),
    watchUpdatingOfqualItemSaga(),
    watchLoadingAoItemSaga(),
    watchUpdatingAoItemSaga(),
    watchRegulatorsToNotifyItemSaga(),
    watchAddCaseNoteSaga(),
    watchSendMessageSaga(),
    watchAddEventSaga(),
    watchCompleteEventSaga(),
    watchSendInternalMessageSaga(),
    watchEnableResubmitSaga(),
    watchSubmitSocSaga(),
    watchCloseSocSaga(),
    watchReopenSocSaga()
    // further sagas...
  ]);
}
